import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/common/state/hooks";
import { NavLink, useLocation } from "react-router-dom";
import {
  getCurrentProjectGroupElement,
  getSelectedProjectId,
} from "../../../features/stats/state/statsSlice";
import { Tooltip } from "../Tooltip/Tooltip";

export type NavigationTabsProps = {
  tabs: {
    title: string;
    urlEndPoint: string;
    hidden?: boolean;
    onClick?: any;
    preventNavigation?: boolean; // If true, renders as button instead of NavLink
    isActive?: boolean; // For preventNavigation tabs: manually control active state
    disabled?: boolean; // If true, disables the tab
    disabledTooltip?: string; // Tooltip message to show when disabled
  }[];
};

const NavigationTabs = ({ tabs }: NavigationTabsProps) => {
  const location = useLocation();
  const { t } = useTranslation();
  const selectedProjectId = useAppSelector(getSelectedProjectId);
  const currentProjectGroupElement = useAppSelector(getCurrentProjectGroupElement);
  const activeClassName =
    "inline-block py-2 px-8 text-green-500 rounded-t-lg border-b-2 border-b-green-500 h-full";
  const inStatsView = location.pathname.includes("statistics");
  // We want to hide tabs while on /statistics Overview
  const isStatisticsOverview = selectedProjectId === 0 && inStatsView;
  // We want to disable 'Participants' tab on !need_login projects
  const shouldDisableParticipants =
    inStatsView && currentProjectGroupElement?.project_group.auth_type === "none";

  // Helper function to check if a tab should be disabled
  const isTabDisabled = (tab: NavigationTabsProps["tabs"][number]) => {
    return tab.disabled || (shouldDisableParticipants && tab.urlEndPoint === "learners");
  };

  // Helper function to get the tooltip message for a disabled tab
  const getDisabledTooltip = (tab: NavigationTabsProps["tabs"][number]) => {
    if (tab.disabled && tab.disabledTooltip) {
      return tab.disabledTooltip;
    }
    if (shouldDisableParticipants && tab.urlEndPoint === "learners") {
      return t("pages.statistics.unavailableForAnonymous");
    }
    return "";
  };
  return (
    <div
      className={`text-sm font-medium text-center text-gray-500 border-b border-gray-200 ${
        isStatisticsOverview ? "mt-8" : ""
      } flex flex-wrap -mb-px
        `}
    >
      {!isStatisticsOverview &&
        tabs.map((tab, i) => {
          const onboardingSteps = (tabTitle: string) => {
            switch (String(tabTitle)) {
              case String(t("general.participants")):
                return "onboardingHowToAddScene-step-3";
              case String(t("general.publish")):
                return "onboardingAboutModules-step-3";
              case String(t("pages.project.projectGroupCollaborators.collaborators")):
                return "onboardingAboutModules-step-2";
              case String(t("general.preview")):
                return "onboardingHowToAddScene-step-2";
              default:
                return "";
            }
          };

          return tab.hidden ? (
            <></>
          ) : tab.preventNavigation ? (
            // Render as button for tabs that prevent navigation (e.g., Preview opens external window)
            <Tooltip key={`${tab.toString()}-${i}`} message={getDisabledTooltip(tab)}>
              <button
                key={i}
                type="button"
                className={
                  tab.isActive
                    ? activeClassName
                    : `inline-block py-2 px-8 rounded-t-lg border-transparent h-full ${onboardingSteps(
                        tab.title,
                      )} ${
                        isTabDisabled(tab)
                          ? "text-gray-300 cursor-not-allowed"
                          : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300 cursor-pointer"
                      }`
                }
                onClick={(e) => {
                  if (isTabDisabled(tab)) {
                    return e.preventDefault();
                  }
                  if (tab?.onClick) {
                    tab.onClick(e);
                  }
                }}
              >
                {tab.title}
              </button>
            </Tooltip>
          ) : (
            // Render as NavLink for normal navigation tabs
            <Tooltip key={`${tab.toString()}-${i}`} message={getDisabledTooltip(tab)}>
              <NavLink
                key={i}
                to={tab.urlEndPoint}
                className={({ isActive }) =>
                  isActive
                    ? activeClassName
                    : `inline-block py-2 px-8 rounded-t-lg border-transparent h-full ${onboardingSteps(
                        tab.title,
                      )} ${
                        isTabDisabled(tab)
                          ? "text-gray-300 cursor-not-allowed"
                          : "hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300"
                      }`
                }
                onClick={(e) => {
                  if (isTabDisabled(tab)) {
                    return e.preventDefault();
                  }
                  if (tab?.onClick) {
                    tab.onClick(e);
                  }
                }}
              >
                {tab.title}
              </NavLink>
            </Tooltip>
          );
        })}
    </div>
  );
};

export default NavigationTabs;
